import * as mutations from "../graphql/mutations";
import KeyboardArrowLeft from "@mui/icons-material/KeyboardArrowLeft";
import KeyboardArrowRight from "@mui/icons-material/KeyboardArrowRight";
import DisplayQuestion from "../components/DisplayQuestion";
import DisplayStructuredEntryQuestion from "../components/DisplayStructuredEntryQuestion";
import { useTranslation } from "react-i18next";
import { apiClient } from "../helpers/apiClient";
import { useAuthInfo } from "../helpers/useAuthInfo";
import { useState, useEffect } from "react";
import {
  Box,
  Button,
  MenuItem,
  MobileStepper,
  Select,
  Typography,
} from "@mui/material";

export default function AnswerQuestion({
  performedAssessmentId,
  assessmentAssignmentID,
  setOpenNotification,
  setNotificationMessage,
  selectedModule,
  setSelectedModule,
}) {
  const { t } = useTranslation();
  const { userGroups } = useAuthInfo();

  const [activeStep, setActiveStep] = useState(0);
  const [percentage, setPercentage] = useState(0);
  const [currentPerformedAssessmentId, setCurrentPerformedAssessmentId] =
    useState(performedAssessmentId);
  const [moduleQuestions, setModuleQuestions] = useState(
    selectedModule.questionWithAnswer || [],
  );
  const [question, setQuestionState] = useState(moduleQuestions[activeStep]);

  function updateStoredQuestion(questionIndex, nextQuestion) {
    setModuleQuestions((prev) =>
      prev.map((item, index) =>
        index === questionIndex ? nextQuestion : item,
      ),
    );
    setSelectedModule((prevSelectedModule) => ({
      ...prevSelectedModule,
      questionWithAnswer: prevSelectedModule.questionWithAnswer.map(
        (item, index) => (index === questionIndex ? nextQuestion : item),
      ),
    }));
  }

  function setQuestion(updater) {
    setQuestionState((prev) => {
      const nextQuestion =
        typeof updater === "function" ? updater(prev) : updater;
      updateStoredQuestion(activeStep, nextQuestion);
      return nextQuestion;
    });
  }

  async function ensurePerformedAssessment() {
    if (currentPerformedAssessmentId) return currentPerformedAssessmentId;

    const performedAssessment = await apiClient.graphql({
      query: mutations.createPerformAssessment,
      variables: {
        input: {
          assessmentState: "IN_PROGRESS",
          assessmentAssignmentID: assessmentAssignmentID,
          ownerGroups: userGroups,
        },
      },
    });

    const newPerformedAssessmentId =
      performedAssessment.data.createPerformAssessment.id;
    setCurrentPerformedAssessmentId(newPerformedAssessmentId);
    return newPerformedAssessmentId;
  }

  async function createUpdateStructuredEntryAnswer() {
    try {
      const currentPerformedAssessmentId = await ensurePerformedAssessment();
      const isExistingAnswer = !!question.answerInfo?.id;
      const payload = {
        answerText: question.answerInfo?.comment || "",
        responsibleRole: question.answerInfo?.contactPerson || "",
        entries: question.requirementsWithAnswers.map(
          ({ requirement, answer }, index) => ({
            ...answer,
            entryIndex: index,
            text: requirement.text,
            impact: requirement.impact,
            risk: answer?.risk ?? requirement.risk ?? "",
            opportunity: answer?.opportunity ?? requirement.opportunity ?? "",
            example: answer?.example ?? requirement.example ?? "",
          }),
        ),
      };

      if (question.answerInfo?.id) {
        await apiClient.graphql({
          query: mutations.updateRequirementAnswer,
          variables: {
            input: {
              id: question.answerInfo.id,
              requirementID: question.question.id,
              performedAssessmentID: currentPerformedAssessmentId,
              fulfilled: true,
              irrelevant: false,
              comment: JSON.stringify(payload),
              contactPerson: payload.responsibleRole,
            },
          },
        });
      } else {
        const createdAnswer = await apiClient.graphql({
          query: mutations.createRequirementAnswer,
          variables: {
            input: {
              requirementID: question.question.id,
              performedAssessmentID: currentPerformedAssessmentId,
              fulfilled: true,
              irrelevant: false,
              comment: JSON.stringify(payload),
              contactPerson: payload.responsibleRole,
              ownerGroups: userGroups,
            },
          },
        });
        setQuestion((prev) => ({
          ...prev,
          answerInfo: {
            ...prev.answerInfo,
            id: createdAnswer.data.createRequirementAnswer.id,
          },
        }));
      }

      if (!isExistingAnswer) {
        setNotificationMessage(t("questionAnswered"));
        setOpenNotification(true);
      }
      nextStep();
    } catch (error) {
      console.error(error);
      setNotificationMessage(t("errorMessage"));
      setOpenNotification(true);
    }
  }

  async function createUpdateAnswer() {
    if (question.question.type === "STRUCTURED_ENTRY") {
      await createUpdateStructuredEntryAnswer();
      return;
    }

    try {
      const currentPerformedAssessmentId = await ensurePerformedAssessment();
      const isExistingAnswer = question.requirementsWithAnswers.some(
        ({ answer }) => answer?.id,
      );

      const updatedRequirementsWithAnswers = await Promise.all(
        question.requirementsWithAnswers.map(async (reqWithAnswer) => {
          const { answer, requirement } = reqWithAnswer;

          if (answer.id) {
            await apiClient.graphql({
              query: mutations.updateRequirementAnswer,
              variables: {
                input: {
                  id: answer.id,
                  requirementID: requirement.id,
                  performedAssessmentID: currentPerformedAssessmentId,
                  selectedLevel: answer.selectedLevel ?? null,
                  fulfilled: answer.fulfilled ?? null,
                  irrelevant: answer.irrelevant ?? false,
                  comment: answer.comment || "",
                  contactPerson: answer.contactPerson || "",
                },
              },
            });
            return reqWithAnswer;
          } else {
            const createdAnswer = await apiClient.graphql({
              query: mutations.createRequirementAnswer,
              variables: {
                input: {
                  requirementID: requirement.id,
                  performedAssessmentID: currentPerformedAssessmentId,
                  selectedLevel: answer.selectedLevel ?? null,
                  fulfilled: answer.fulfilled ?? null,
                  irrelevant: answer.irrelevant ?? false,
                  comment: answer.comment || "",
                  contactPerson: answer.contactPerson || "",
                  ownerGroups: userGroups,
                },
              },
            });
            return {
              ...reqWithAnswer,
              answer: {
                ...answer,
                id: createdAnswer.data.createRequirementAnswer.id,
              },
            };
          }
        }),
      );

      setQuestion((prev) => ({
        ...prev,
        requirementsWithAnswers: updatedRequirementsWithAnswers,
      }));

      if (!isExistingAnswer) {
        setNotificationMessage(t("questionAnswered"));
        setOpenNotification(true);
      }
    } catch (error) {
      console.error(error);
      setNotificationMessage(t("errorMessage"));
      setOpenNotification(true);
    }

    nextStep();
  }

  function nextStep() {
    const nextStep = activeStep + 1;
    if (nextStep === moduleQuestions.length) {
      window.location.replace("/performAssessment/" + assessmentAssignmentID);
    } else {
      setActiveStep(nextStep);
    }
  }

  useEffect(() => {
    setQuestionState(moduleQuestions[activeStep]);
  }, [activeStep, moduleQuestions]);

  if (!question) {
    return null;
  }

  return (
    <Box sx={{ display: "flex", flexDirection: "column" }}>
      <Box
        sx={{
          display: "flex",
          flexDirection: "row",
          justifyContent: "flex-end",
          alignItems: "center",
          mt: 1,
        }}
      >
        <Box
          sx={{
            display: "flex",
            flexDirection: "row",
            alignItems: "center",
          }}
        >
          <Typography sx={{ width: 230, userSelect: "none" }}>
            {`${t("jumpTo")} ${t("question")}`}
          </Typography>
          <Select
            notched={true}
            value={activeStep}
            sx={{ width: 1, height: 35 }}
            onChange={(event) => {
              setActiveStep(event.target.value);
            }}
          >
            {moduleQuestions.map((q, index) => (
              <MenuItem value={index} key={index}>
                {`${t("question")} Nr. ${index + 1}`}
              </MenuItem>
            ))}
          </Select>
        </Box>
      </Box>
      <Box sx={{ display: "flex", flexDirection: "row" }}>
        <Typography
          sx={{
            fontSize: 21,
            fontWeight: "bold",
            userSelect: "none",
          }}
        >
          {`${t("module")} "${selectedModule.module.title}":`}
        </Typography>
      </Box>
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          width: 1,
          mt: 1.5,
        }}
      >
        {question.question.type === "STRUCTURED_ENTRY" ? (
          <DisplayStructuredEntryQuestion
            questionData={question}
            setQuestion={setQuestion}
            index={activeStep}
          />
        ) : (
          <DisplayQuestion
            questionData={question}
            setQuestion={setQuestion}
            index={activeStep}
            percentage={percentage}
            setPercentage={setPercentage}
          />
        )}
        <Box
          sx={{
            display: "flex",
            flexDirection: "row",
            width: 1,
            justifyContent: "center",
          }}
        >
          <Button
            variant="contained"
            sx={{ borderRadius: 20 }}
            onClick={() => createUpdateAnswer()}
          >
            {t("saveAnswer")}
          </Button>
        </Box>
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            width: 1,
            alignItems: "center",
            mt: 5,
          }}
        >
          <Typography
            sx={{
              fontSize: 17,
              mb: -2,
              zIndex: 100,
              userSelect: "none",
            }}
          >
            {t("question")} {activeStep + 1}/
            {moduleQuestions.length}
          </Typography>
          <MobileStepper
            variant="progress"
            steps={moduleQuestions.length}
            position="static"
            activeStep={activeStep}
            sx={{ width: 850, flexGrow: 1 }}
            nextButton={
              <Button
                size="small"
                onClick={() => nextStep()}
                sx={{
                  width: 200,
                  borderTopRightRadius: 20,
                  borderBottomRightRadius: 20,
                }}
              >
                {activeStep === moduleQuestions.length - 1
                  ? t("backToModuleSelection")
                  : t("next")}
                <KeyboardArrowRight />
              </Button>
            }
            backButton={
              <Button
                size="small"
                onClick={() => setActiveStep(activeStep - 1)}
                disabled={activeStep === 0}
                sx={{
                  width: 200,
                  borderTopLeftRadius: 20,
                  borderBottomLeftRadius: 20,
                }}
              >
                <KeyboardArrowLeft />
                {t("back")}
              </Button>
            }
          />
        </Box>
      </Box>
    </Box>
  );
}

/*function DisplayQuestion({
  setQuestion,
  questionData,
  index,
  setPercentage,
  percentage,
  language,
}) {
  const { t } = useTranslation();

  const handleAnswerInfoDataChange = (field, value) => {
    setQuestion((prev) => ({
      ...prev,
      answerInfo: {
        ...prev.answerInfo,
        [field]: value,
      },
    }));
  };

  const toggleIrrelevant = (requirementId, newValue) => {
    setQuestion((prev) => {
      const updated = prev.requirementsWithAnswers.map((item) => {
        if (item.answer.requirementID === requirementId) {
          return {
            ...item,
            answer: {
              ...item.answer,
              fulfilled: !newValue,
              irrelevant: newValue,
            },
          };
        }
        return item;
      });

      return {
        ...prev,
        requirementsWithAnswers: updated,
      };
    });
  };

  const handleSliderChange = (requirementID, newValue) => {
    setQuestion((prev) => {
      const updated = prev.requirementsWithAnswers.map((item) => {
        if (item.answer.requirementID === requirementID) {
          return {
            ...item,
            answer: {
              ...item.answer,
              fulfilled: true,
              fulfillment: Number(newValue),
            },
          };
        }
        return item;
      });

      return {
        ...prev,
        requirementsWithAnswers: updated,
      };
    });
  };

  const handleRequirementChange = (requirementID, event) => {
    const selected = event.target.value;

    setQuestion((prev) => ({
      ...prev,
      requirementsWithAnswers: prev.requirementsWithAnswers.map((item) => {
        if (item.requirement.id !== requirementID) return item;

        let updatedAnswer;

        if (selected === t("fulfilled")) {
          updatedAnswer = {
            ...item.answer,
            fulfilled: true,
            fulfillment: 1,
            irrelevant: false,
          };
        } else if (selected === t("notFulfilled")) {
          updatedAnswer = {
            ...item.answer,
            fulfilled: false,
            fulfillment: 0,
            irrelevant: false,
          };
        } else if (selected === t("notRelevant")) {
          updatedAnswer = { ...item.answer, fulfilled: null, irrelevant: true };
        } else {
          updatedAnswer = item.answer; // fallback
        }

        return {
          ...item,
          answer: updatedAnswer,
        };
      }),
    }));
  };

  function calculatePercentage() {
    let sum = 0;
    let total = 0;

    questionData.requirementsWithAnswers.map(({ requirement, answer }) => {
      if (!answer.irrelevant) {
        total += Number(requirement.weight);
        if (answer.fulfilled) {
          if (requirement.gradations) {
            sum += Number(
              (requirement.weight * answer.fulfillment) /
                (requirement.recommendations.length - 1 || 1)
            );
          } else {
            sum += Number(requirement.weight);
          }
        }
      }
    });
    setPercentage((sum / total) * 100);
  }

  useEffect(() => {
    calculatePercentage();
  }, [questionData]);

  const buildMarks = (recommendations) =>
    recommendations.map((_, index) => ({
      value: index,
      label: String(index),
    }));

  const getRadioValueForRequirement = (requirementID) => {
    const item = questionData.requirementsWithAnswers.find(
      (entry) => entry.requirement.id === requirementID
    );

    if (!item || !item.answer) return "";

    const { fulfilled, irrelevant } = item.answer;

    if (irrelevant) return t("notRelevant");
    if (fulfilled === true) return t("fulfilled");
    if (fulfilled === false) return t("notFulfilled");

    return "";
  };

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        width: 1,
        mb: 5,
      }}
    >
      <Box sx={{ display: "flex", flexDirection: "row" }}>
        <Typography
          sx={{ fontWeight: "bold", textDecoration: "underline", fontSize: 16 }}
        >
          {`${t("question")} Nr. ${index + 1}`}
        </Typography>
      </Box>
      <Box
        sx={{
          display: "flex",
          flexDirection: "row",
          justifyContent: "flex-end",
        }}
      >
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            borderBottom: 1,
            borderColor: "#ccc",
          }}
        >
          <Typography sx={{ userSelect: "none" }}>
            {`${t("fulfilledPercent")}`}
          </Typography>
          <LinearProgressWithLabel progressValue={percentage} />
        </Box>
      </Box>
      <Box
        sx={{
          display: "flex",
          flexDirection: "row",
          justifyContent: "center",
          mt: 2,
        }}
      >
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            minWidth: 1 / 2,
          }}
        >
          <Typography
            sx={{ maxWidth: 1000, fontSize: 16, ml: 4, whiteSpace: "pre-line" }}
          >
            {questionData?.question.text}
          </Typography>
          <Box
            sx={{
              flexDirection: "column",
              display: "flex",
              justifyContent: "center",
              width: 1,
              mt: 5,
            }}
          >
            <Box
              sx={{
                display: "flex",
                flexDirection: "row",
                ml: 2,
              }}
            >
              <TextField
                color="secondary"
                InputLabelProps={{
                  shrink: true,
                }}
                label={t("contactPerson")}
                variant="outlined"
                size="small"
                sx={{ width: 1 }}
                value={questionData?.answerInfo.contactPerson}
                onChange={(event) =>
                  handleAnswerInfoDataChange("contactPerson", event.target.value)
                }
              />
            </Box>
            <TextField
              color="secondary"
              InputLabelProps={{
                shrink: true,
              }}
              label={t("comments")}
              variant="outlined"
              size="small"
              multiline
              minRows={5}
              maxRows={5}
              sx={{ ml: 2, mt: 2 }}
              value={questionData.answerInfo.comment}
              onChange={(event) =>
                handleAnswerInfoDataChange("comment", event.target.value)
              }
            />
          </Box>
          {questionData.question?.source && (
            <Typography
              sx={{ maxWidth: 1000, fontSize: 16, ml: 4, mt: 5, color: "#999" }}
            >
              {`${questionData.question.text} (${t("source")}: ${
                questionData.question.source
                  ? questionData.question.source
                  : t("none")
              })`}
            </Typography>
          )}
          <Transcriptions selectedLanguage={language} />
        </Box>
        {questionData?.requirementsWithAnswers &&
          questionData.requirementsWithAnswers.length > 0 && (
            <Box
              sx={{
                borderLeft: 1,
                pl: 3,
                ml: 3,
                borderColor: "#ccc",
                minWidth: 1 / 2,
                overflow: "visible",
              }}
            >
              <Typography
                sx={{
                  fontSize: 17,
                  textDecoration: "underline",
                  whiteSpace: "nowrap",
                  userSelect: "none",
                }}
              >
                {`${t("requirements")}:`}
              </Typography>
              <Box
                sx={{
                  display: "flex",
                  borderBottom: 1,
                  borderColor: "#ccc",
                }}
              >
                <List
                  sx={{
                    ml: -1,
                    pb: 0,
                    width: "100%",
                  }}
                >
                  {questionData?.requirementsWithAnswers
                    ?.sort((a, b) =>
                      sortRequirementsByWeight(a.requirement, b.requirement)
                    )
                    .map(({ requirement, answer }, index) => (
                      <ListItem
                        sx={{
                          display: "flex",
                          pl: 0.5,
                          py: 0,
                          flexDirection: "row",
                          mt: index === 0 ? 0 : 0.5,
                          pt: index === 0 ? 0 : 0.5,
                          borderTop: index !== 0 ? 1 : 0,
                          borderColor: "#dddddd",
                        }}
                        key={index}
                      >
                        <Box
                          sx={{
                            display: "flex",
                            flexDirection: "column",
                            width: "25%",
                            borderRight: 1,
                            borderColor: "#ccc",
                            mr: 1,
                            pr: 1,
                          }}
                        >
                          <Box
                            sx={{
                              display: "flex",
                              flexDirection: "row",
                              alignItems: "center",
                              justifyContent: "space-between",
                              overflow: "visible",
                            }}
                          >
                            {requirement.gradations ? (
                              <Box
                                sx={{
                                  display: "flex",
                                  flexDirection: "column",
                                  width: 1,
                                }}
                              >
                                <Slider
                                  size="small"
                                  sx={{
                                    width: 9 / 10,
                                    ml: 0.6,
                                    mb: 1.25,
                                    "& .MuiSlider-markLabel": {
                                      marginTop: -1.2,
                                      fontSize: 12,
                                      userSelect: "none",
                                    },
                                  }}
                                  valueLabelFormat={(value) => `${value}`}
                                  valueLabelDisplay="auto"
                                  value={answer?.fulfillment || 0}
                                  disabled={answer?.irrelevant}
                                  step={1}
                                  marks={buildMarks(
                                    requirement.recommendations || []
                                  )}
                                  min={0}
                                  max={
                                    requirement.recommendations
                                      ? requirement.recommendations.length - 1
                                      : 0
                                  }
                                  onChange={(e) =>
                                    handleSliderChange(
                                      requirement.id,
                                      e.target.value
                                    )
                                  }
                                />
                                <Checkbox
                                  size="small"
                                  checked={answer?.irrelevant || false}
                                  sx={{
                                    color: "#dddddd",
                                    "&.Mui-checked": {
                                      color: "#000000",
                                    },
                                    height: 30,
                                    width: 30,
                                  }}
                                  icon={<NotInterestedIcon />}
                                  checkedIcon={<NotInterestedIcon />}
                                  onClick={(e) => {
                                    toggleIrrelevant(
                                      requirement.id,
                                      e.target.checked
                                    );
                                  }}
                                />
                              </Box>
                            ) : (
                              <Box
                                sx={{
                                  display: "flex",
                                  flexDirection: "column",
                                  width: "100%",
                                }}
                              >
                                <FormControl>
                                  <RadioGroup
                                    aria-labelledby="demo-radio-buttons-group-label"
                                    value={getRadioValueForRequirement(
                                      requirement.id
                                    )}
                                    onChange={(e) =>
                                      handleRequirementChange(requirement.id, e)
                                    }
                                    name="radio-buttons-group"
                                  >
                                    <FormControlLabel
                                      value={t("fulfilled")}
                                      control={<Radio color="success" />}
                                      label={t("fulfilled")}
                                    />
                                    <FormControlLabel
                                      value={t("notFulfilled")}
                                      control={<Radio color="secondary" />}
                                      label={t("notFulfilled")}
                                    />
                                    <FormControlLabel
                                      value={t("notRelevant")}
                                      control={<Radio color="default" />}
                                      label={t("notRelevant")}
                                    />
                                  </RadioGroup>
                                </FormControl>
                              </Box>
                            )}
                          </Box>
                        </Box>
                        <Box
                          sx={{
                            display: "flex",
                            justifyContent: "flex-start",
                            width: "75%",
                          }}
                        >
                          <List
                            sx={{
                              listStyleType: "circle",
                              p: 0,
                              ml: 2,
                            }}
                          >
                            <Typography
                              key={index}
                              sx={{
                                ml: 1,
                                color: requirement.irrelevant
                                  ? "#999999"
                                  : "#000000",
                              }}
                            >
                              {requirement.text}
                            </Typography>
                          </List>
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              flexDirection: "row",
                            }}
                          >
                            <Typography
                              sx={{
                                ml: 1,
                                color: requirement.irrelevant
                                  ? "#999999"
                                  : "#000000",
                              }}
                            >
                              {` (${t("weight")}: ${requirement.weight})`}
                            </Typography>
                          </Box>
                        </Box>
                      </ListItem>
                    ))}
                </List>
              </Box>
            </Box>
          )}
      </Box>
      <Divider flexItem orientation="horizontal" sx={{ mt: 4, mx: 2 }} />
    </Box>
  );
}

*/
